// src/pages/Admin/UsersRegistered.jsx
import React, { useEffect, useState } from 'react';
import { db, auth } from '../../utils/firebase';
import { ref, get } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const UsersRegistered = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);
  const [users, setUsers] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [ownProfileHint, setOwnProfileHint] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;

    let cancelled = false;
    setLoadError(null);
    setOwnProfileHint(null);

    (async () => {
      try {
        const u = auth.currentUser;
        if (!u) return;
        await u.getIdToken(true);
        if (cancelled) return;
        const snapshot = await get(ref(db, '/profiles'));
        if (cancelled) return;
        const data = snapshot.val() || {};
        const list = Object.entries(data).map(([id, row]) => ({ id, ...row }));
        setUsers(list);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        setUsers([]);
        const msg = err?.message || String(err);
        if (msg === 'Permission denied') {
          setLoadError('permission_denied');
          try {
            const mine = await get(ref(db, `profiles/${user.uid}`));
            if (cancelled) return;
            const v = mine.exists() ? mine.val() : null;
            setOwnProfileHint(
              v == null
                ? 'No row at profiles/{your uid} — create it or sign in again.'
                : `profiles/${user.uid} has isAdmin=${JSON.stringify(v?.isAdmin)} (${typeof v?.isAdmin}). If bulk list still fails: (1) Publish latest database.rules.json. (2) In Data add admins/${user.uid} = true (boolean) — rules treat that as admin. (3) Compare this UID letter-by-letter with Authentication → User UID (0 vs O, 1 vs l).`
            );
          } catch {
            setOwnProfileHint('Could not read your own profile either — check Rules and auth.');
          }
        } else {
          setLoadError(msg || 'Failed to load users');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t('Utilisateurs inscrits', 'Registered users')}</h1>
        <p className="mt-1 text-sm text-slate-500">{t('Profils stockés dans Realtime Database', 'Profiles stored in Realtime Database')}</p>
      </div>

      {loadError === 'permission_denied' ? (
        <div className="rounded-2xl border border-amber-800/50 bg-amber-950/25 p-5 text-sm text-amber-100">
          <p className="font-semibold text-amber-50">Firebase: permission denied on /profiles</p>
          <p className="mt-2 text-amber-100/90">
            Listing all users requires each profile child to allow read for admins. In Realtime Database rules, use something like:
          </p>
          <pre className="mt-3 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/80 p-4 text-xs text-slate-200">
{`"admins": { "$uid": { ".read": "auth != null && auth.uid == $uid", ".write": false } },
"profiles": { "$uid": { ".read": "… || root.child('admins').child(auth.uid).val() == true || …" } }`}
          </pre>
          <p className="mt-2 text-xs text-amber-200/80">
            Copy the full <code className="rounded bg-black/30 px-1 text-amber-100">database.rules.json</code> and Publish. Then in <strong>Data</strong> add node <code className="rounded bg-black/30 px-1 text-amber-100">admins</code> → your Auth UID (from sidebar) → value <code className="rounded bg-black/30 px-1 text-amber-100">true</code> (boolean).
          </p>
          {ownProfileHint ? <p className="mt-3 rounded-lg border border-amber-900/40 bg-black/20 p-3 text-xs text-amber-50/95">{ownProfileHint}</p> : null}
          <p className="mt-2 text-amber-100/80">
            Remove blanket <code className="text-amber-200">.read</code> at the database root if it blocks public reads you need elsewhere (e.g. channel cache).
          </p>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-amber-100/90">
            <li>
              <strong>Rules:</strong> paste all of <code className="text-amber-200">database.rules.json</code> → Publish. Remove any top-level <code className="text-amber-200">.read</code>/<code className="text-amber-200">.write</code> on the root that blocks children.
            </li>
            <li>
              <strong>Data:</strong> add child <code className="text-amber-200">admins</code>, then a key equal to your Auth UID (sidebar), value boolean <code className="text-amber-200">true</code>. The console can create this; client apps cannot write <code className="text-amber-200">admins</code>.
            </li>
          </ol>
        </div>
      ) : loadError ? (
        <p className="rounded-lg border border-rose-900/50 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">{loadError}</p>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/40 shadow-panel">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-900/80">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{t('E-mail', 'Email')}</th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{t('Inscrit le', 'Joined')}</th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{t('Admin', 'Admin')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80">
              {users.map((row) => (
                <tr key={row.id} className="transition hover:bg-slate-900/60">
                  <td className="whitespace-nowrap px-5 py-3.5 text-slate-200">{row.email}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-slate-400">
                    {new Date(row.signupDate || row.createdAt || 0).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        row.isAdmin ? 'bg-emerald-950/60 text-emerald-300 ring-1 ring-emerald-800/50' : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {row.isAdmin ? t('Oui', 'Yes') : t('Non', 'No')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersRegistered;
